import React from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Chip,
  Box,
  Avatar,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Visibility as ViewIcon,
  Person as PersonIcon,
  PhotoCamera as PhotoIcon,
  Link as RelationshipIcon,
  Event as EventIcon,
  Description as DocumentIcon,
  AutoStories as MemoryIcon,
  StickyNote2 as NoteIcon,
  Forum as CommentIcon,
  Science as ResearchIcon,
} from '@mui/icons-material';
import { Change, ChangeEntityType } from '../../types';
import { formatDistanceToNow } from 'date-fns';
import { SourceBadge } from './SourceBadge';

/**
 * ChangeCard - Display component for a single change
 *
 * Shows change summary with quick action buttons for approve/reject
 */

interface ChangeCardProps {
  change: Change;
  onApprove: (changeId: string) => void;
  onReject: (changeId: string) => void;
  onViewDetails: (change: Change) => void;
  showActions?: boolean;
}

export const ChangeCard: React.FC<ChangeCardProps> = ({
  change,
  onApprove,
  onReject,
  onViewDetails,
  showActions = true,
}) => {
  // ===== Helper Functions =====

  const getEntityIcon = (entityType: ChangeEntityType) => {
    const iconMap: Record<ChangeEntityType, React.ReactElement> = {
      person: <PersonIcon />,
      photo: <PhotoIcon />,
      relationship: <RelationshipIcon />,
      lifeEvent: <EventIcon />,
      comment: <EventIcon />,
      user: <PersonIcon />,
      album: <PhotoIcon />,
      story: <EventIcon />,
      document: <DocumentIcon />,
      memory: <MemoryIcon />,
      note: <NoteIcon />,
      profileComment: <CommentIcon />,
      research: <ResearchIcon />,
    };
    return iconMap[entityType] || <PersonIcon />;
  };

  const getChangeTypeColor = (changeType: string) => {
    const colorMap = {
      create: 'success',
      update: 'info',
      delete: 'error',
      restore: 'warning',
      merge: 'primary',
    };
    return colorMap[changeType as keyof typeof colorMap] || 'default';
  };

  const getStatusColor = (status: string) => {
    const colorMap = {
      pending: 'warning',
      approved: 'success',
      rejected: 'error',
      'auto-approved': 'info',
      expired: 'default',
    };
    return colorMap[status as keyof typeof colorMap] || 'default';
  };

  const getChangeDescription = () => {
    const entityName = change.afterState?.firstName && change.afterState?.lastName
      ? `${change.afterState.firstName} ${change.afterState.lastName}`
      : change.afterState?.title || change.entityId;

    const actionMap = {
      create: 'created',
      update: 'updated',
      delete: 'deleted',
      restore: 'restored',
      merge: 'merged',
    };

    const action = actionMap[change.changeType as keyof typeof actionMap] || change.changeType;
    const entity = change.entityType.charAt(0).toUpperCase() + change.entityType.slice(1);

    return `${action} ${entity}: ${entityName}`;
  };

  const getFieldSummary = () => {
    if (!change.changedFields || change.changedFields.length === 0) {
      return null;
    }

    const fieldCount = change.changedFields.length;
    const preview = change.changedFields.slice(0, 3).join(', ');
    const remaining = fieldCount > 3 ? ` +${fieldCount - 3} more` : '';

    return `Fields: ${preview}${remaining}`;
  };

  const timeAgo = formatDistanceToNow(new Date(change.createdAt), { addSuffix: true });

  // ===== Event Handlers =====

  const handleApprove = (e: React.MouseEvent) => {
    e.stopPropagation();
    onApprove(change.id);
  };

  const handleReject = (e: React.MouseEvent) => {
    e.stopPropagation();
    onReject(change.id);
  };

  const handleViewDetails = () => {
    onViewDetails(change);
  };

  // ===== Render =====

  return (
    <Card
      sx={{
        mb: 2,
        '&:hover': {
          boxShadow: 3,
          cursor: 'pointer',
        },
      }}
      onClick={handleViewDetails}
    >
      <CardContent>
        <Box display="flex" alignItems="flex-start" gap={2}>
          {/* Entity Icon */}
          <Avatar sx={{ bgcolor: 'primary.main' }}>
            {getEntityIcon(change.entityType)}
          </Avatar>

          {/* Change Details */}
          <Box flex={1}>
            <Box display="flex" alignItems="center" gap={1} mb={1} flexWrap="wrap">
              <Typography variant="h6" component="div">
                {getChangeDescription()}
              </Typography>
              <Chip
                label={change.changeType}
                color={getChangeTypeColor(change.changeType) as any}
                size="small"
              />
              <Chip
                label={change.status}
                color={getStatusColor(change.status) as any}
                size="small"
                variant="outlined"
              />
              {/* Source Badge (Phase 4.3) */}
              {change.source && (
                <SourceBadge
                  source={change.source}
                  sourceDetails={change.sourceDetails}
                  size="small"
                  showIcon={true}
                />
              )}
            </Box>

            <Typography variant="body2" color="text.secondary" gutterBottom>
              By {change.userName} • {timeAgo}
            </Typography>

            {getFieldSummary() && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {getFieldSummary()}
              </Typography>
            )}

            {change.reason && (
              <Typography
                variant="body2"
                sx={{ mt: 1, fontStyle: 'italic', color: 'text.secondary' }}
              >
                Reason: {change.reason}
              </Typography>
            )}

            {change.reviewNotes && (
              <Box
                sx={{
                  mt: 1,
                  p: 1,
                  bgcolor: 'grey.100',
                  borderRadius: 1,
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  Review Notes:
                </Typography>
                <Typography variant="body2">{change.reviewNotes}</Typography>
              </Box>
            )}
          </Box>
        </Box>
      </CardContent>

      {/* Actions */}
      {showActions && change.status === 'pending' && (
        <CardActions sx={{ justifyContent: 'flex-end', pt: 0 }}>
          <Tooltip title="View Details">
            <IconButton onClick={handleViewDetails} size="small">
              <ViewIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Approve Change">
            <Button
              startIcon={<ApproveIcon />}
              color="success"
              variant="contained"
              size="small"
              onClick={handleApprove}
            >
              Approve
            </Button>
          </Tooltip>
          <Tooltip title="Reject Change">
            <Button
              startIcon={<RejectIcon />}
              color="error"
              variant="outlined"
              size="small"
              onClick={handleReject}
            >
              Reject
            </Button>
          </Tooltip>
        </CardActions>
      )}

      {/* Non-pending status - show view details only */}
      {showActions && change.status !== 'pending' && (
        <CardActions sx={{ justifyContent: 'flex-end', pt: 0 }}>
          <Button
            startIcon={<ViewIcon />}
            size="small"
            onClick={handleViewDetails}
          >
            View Details
          </Button>
        </CardActions>
      )}
    </Card>
  );
};
